import React, { useEffect, useState } from 'react';
import banner from 'assets/authentication/banner.png';
import trustBanner from 'assets/authentication/trust-banner.png';
import { BiSolidLeftArrow } from 'react-icons/bi';
import { EnterPhone } from './enterPhone/enterPhone.index';
import { EnterCode } from './enterCode/enterCode.index';
import EnterUserInfo from './enterUserInfo/enterUserInfo.index';
import { Link, useNavigate } from 'react-router-dom';
import EnterPassword from './enterPassword/enterPassword.index';
import SetPassword from './setPassword/setPassword.index';
import { ReduxState } from '../../interfaces';
import { connect, ConnectedProps } from 'react-redux';
import { authToken, sendUserDataToLandingPage, userPhoneNumber } from 'scripts';
import { getProfile } from '../../services';

export enum Stage {
    EnterPhone,
    EnterCode,
    EnterPassword,
    SetPassword,
    EnterUserInfo,
}

const renderTitle = (stage: Stage) => {
    switch (stage) {
        case Stage.EnterPhone:
            return 'ورود / ثبت نام';
        case Stage.EnterPassword:
            return 'ورود با رمز عبور';
        case Stage.EnterCode:
            return 'ورود با کد یکبار مصرف';
        case Stage.SetPassword:
            return 'تعیین رمز عبور جدید';
        case Stage.EnterUserInfo:
            return 'اطلاعات کاربری';
        default:
            return '';
    }
};

const Authentication: React.FC<ConnectedProps<typeof connector>> = function (props: ConnectedProps<typeof connector>) {
    const [stage, setStage] = useState<Stage>(Stage.EnterPhone);
    const [phoneNumber, setPhoneNumber] = useState<string>();
    const [isRegister, setIsRegister] = useState<boolean>();
    const [hasPassword, setHasPassword] = useState<boolean>();
    const [isTwoFactorEnabled, setIsTwoFactorEnabled] = useState<boolean>();
    const [codeStage, setCodeStage] = useState<'register' | '2fa' | 'forgot' | 'once'>('once');

    useEffect(() => {
        document.body.style.overflow = 'auto';
        sendUserDataToLandingPage(null);
    }, []);

    const navigate = useNavigate();

    const renderStage = (stage: Stage) => {
        switch (stage) {
            case Stage.EnterPhone:
                return <EnterPhone onComplete={onEnterPhoneComplete} />;
            case Stage.EnterPassword:
                return (
                    <EnterPassword
                        phoneNumber={phoneNumber}
                        onForgotPasswordClick={onForgotPasswordClick}
                        onEnterCodeClick={onEnterCodeClick}
                        onComplete={onEnterPasswordComplete}
                    />
                );
            case Stage.EnterCode:
                return (
                    <EnterCode
                        key={codeStage}
                        stage={codeStage}
                        phoneNumber={phoneNumber}
                        isRegister={isRegister}
                        hasPassword={hasPassword}
                        onEditPhoneNumberClick={() => setStage(Stage.EnterPhone)}
                        onForgotPasswordComplete={onForgotPasswordComplete}
                        onRegisterComplete={onRegisterComplete}
                        onLoginWithCodeComplete={onLoginWithCodeComplete}
                        onSignInVerify2FaComplete={onSignInVerify2FaComplete}
                        isTwoFactorEnabled={isTwoFactorEnabled}
                    />
                );
            case Stage.SetPassword:
                return <SetPassword onComplete={onSetPasswordComplete} />;
            case Stage.EnterUserInfo:
                return <EnterUserInfo onComplete={onEnterUserInfoComplete} onSkip={onEnterUserInfoSkip} />;
            default:
                return <></>;
        }
    };

    const onBackClick = () => {
        switch (stage) {
            case Stage.EnterPhone:
                navigate(-1);
                break;
            case Stage.EnterCode:
                setStage(Stage.EnterPhone);
                break;
            case Stage.EnterPassword:
                setStage(Stage.EnterPhone);
                break;
            case Stage.SetPassword:
                setStage(Stage.EnterPhone);
                break;
        }
    };

    const onEnterPhoneComplete = (isRegister: boolean, hasPassword: boolean, phoneNumber: string) => {
        userPhoneNumber.set(phoneNumber);
        setPhoneNumber(phoneNumber);
        setIsRegister(isRegister);
        setHasPassword(hasPassword);

        if (isRegister) {
            if (hasPassword) setStage(Stage.EnterPassword);
            if (!hasPassword) setStage(Stage.EnterCode);
        } else {
            setCodeStage('register');
            setStage(Stage.EnterCode);
        }
    };

    const onForgotPasswordClick = () => {
        setCodeStage('forgot');
        setStage(Stage.EnterCode);
    };

    const onEnterCodeClick = () => {
        setCodeStage('once');
        setStage(Stage.EnterCode);
    };

    const onLoginWithCodeComplete = (isTwoFactorEnabled: boolean) => {
        setIsTwoFactorEnabled(isTwoFactorEnabled);
        if (isTwoFactorEnabled) {
            setCodeStage('2fa');
        } else {
            getProfile(props.dispatch);
        }
    };

    const onSignInVerify2FaComplete = () => {
        if (hasPassword === false) setStage(Stage.SetPassword);
        else getProfile(props.dispatch);
    };

    const onRegisterComplete = (isTwoFactorEnabled?: boolean) => {
        setStage(Stage.SetPassword);
        setCodeStage('once');
    };

    const onForgotPasswordComplete = (accessToken: string) => {
        const currentToken = authToken.get();

        if (accessToken) {
            setStage(Stage.SetPassword);
            authToken.set({ ...currentToken, accessToken: accessToken });
        }
    };

    const onEnterUserInfoComplete = () => {
        getProfile(props.dispatch);
    };

    const onEnterUserInfoSkip = () => {
        getProfile(props.dispatch);
    };

    const onSetPasswordComplete = () => {
        setStage(Stage.EnterUserInfo);
    };

    const onEnterPasswordComplete = (isTwoFactorEnabled?: boolean) => {
        setIsTwoFactorEnabled(isTwoFactorEnabled);
        if (isTwoFactorEnabled) {
            setCodeStage('2fa');
            setStage(Stage.EnterCode);
        } else {
            getProfile(props.dispatch);
        }
    };

    return (
        <div className={'flex w-full h-screen p-16 bg-background max-lg:p-8'}>
            <div className={'flex flex-col w-1/2 max-lg:w-full'}>
                <div className={'m-auto w-full'}>
                    <div
                        className={
                            'flex flex-col xl:w-2/3 w-[80%] m-auto border border-primary-20 rounded-2xl p-10 bg-white max-lg:w-full'
                        }
                    >
                        {stage === Stage.EnterUserInfo && (
                            <img
                                src={trustBanner}
                                alt="30Pay Trust Banner"
                                className="mb-8 h-[110px] max-lg:h-[95px] object-cover rounded-2xl"
                            />
                        )}
                        <div className={'flex w-full justify-between'}>
                            <h4 className={'text-[24px] font-semibold max-lg:text-[20px]'}>{renderTitle(stage)}</h4>
                            <a
                                className={'flex items-center gap-1 text-primary-80 font-light text-[14px] clickable'}
                                onClick={onBackClick}
                            >
                                بازگشت
                                <BiSolidLeftArrow />
                            </a>
                        </div>
                        {renderStage(stage)}
                    </div>
                    <div
                        className={
                            'w-2/3 text-xs flex items-center justify-between mx-auto mt-6 font-light max-lg:flex-col max-lg:text-center max-lg:w-full max-lg:gap-3'
                        }
                    >
                        <p className={'text-darkPurple-80'}>مطمئن شوید در دامنه رسمی سی پِی هستید</p>
                        <Link to={window.location.origin}>
                            <span className={'clickable text-primary-100'}>{window.location.origin}</span>
                        </Link>
                    </div>
                </div>
            </div>
            <div className={'flex rounded-3xl relative w-1/2 max-lg:hidden overflow-hidden'}>
                <img
                    className={'w-full h-full absolute object-cover rounded-3xl'}
                    src={banner}
                    alt={'Authentication Banner'}
                />
                <div
                    className={
                        'flex flex-col relative z-10 self-end w-full text-right p-4 xl:p-16 !pt-[200px] bg-gradient-to-t from-primaryGradient-1 to-[#205bfc00]'
                    }
                >
                    <h1 className={'text-white text-[40px] font-bold'}>
                        تبادل ارزی ۳۰ پِــی
                        <br />
                        تضمین بهترین نرخ بازار
                    </h1>
                    <p className={'text-white text-[20px] mt-4 font-light'}>
                        تنها در ۳۰ ثانیه با چند کلیک، آسان و مطمئن ارز دیجیتال را خرید و فروش کنید
                    </p>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: ReduxState) => ({ isAuth: state.authStatus, user: state.userData });

const connector = connect(mapStateToProps);
export default connector(Authentication);
